import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useScrollLock } from "../../../hooks/useScrollLock";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

import {
  FiPackage,
  FiShoppingCart,
  FiTrendingUp,
  FiDollarSign,
  FiTarget,
  FiActivity,
  FiUser,
  FiCalendar,
  FiCheck,
  FiClock,
  FiXCircle,
  FiEye,
  FiX,
  FiLock,
} from "react-icons/fi";
import {
  Btn,
  ChartContainer,
  ChartsGrid,
  ChartTitle,
  OrderStatus,
  PageSection,
  StatCard,
  StatIcon,
  StatLabel,
  StatsGrid,
  StatValue,
  Table,
  TableContainer,
  TableHeader,
  TableTitle,
  OrderDetailCard,
  OrderDetailLabel,
  OrderDetailValue,
  OrderItemsSection,
  OrderItemCard,
  OrderItemImage,
  OrderItemInfo,
  OrderItemName,
  OrderItemPrice,
  OrderItemQuantity,
  OrderItemSize,
  CustomerInfoSection,
  CustomerInfoGrid,
  OrderSummarySection,
  OrderSummaryGrid,
  CloseBtn,
  TableTh,
  TableRow,
  TableCell,
  LoadingContainer,
  ChartLockOverlay,
  ChartBlur,
  ChartLockIcon,
  ChartBackground,
} from "./BandDashboardPage.styles";
import { formatPrice } from "../../../utils/utils";
import { useLocation, useNavigate } from "react-router-dom";
import { selectUser } from "../../../store/slices/auth/slice";
import {
  getBandAnalytics,
  getBandProductAnalytics,
  getBandProducts,
  getBandOrders,
  getMonthlyBandRevenuesByPeriod,
} from "../../../store/slices/bandDashboard/asyncThunks";

import { getCategories } from "../../../store/slices/products/asyncThunks";
import {
  selectBandAnalytics,
  selectBandProductAnalytics,
  selectBandProducts,
  selectBandOrders,
  clearAllErrors,
  selectMonthlyBandRevenuesByPeriod,
} from "../../../store/slices/bandDashboard/slice";
import { getBandViewStats } from "../../../store/slices/viewTracking/asyncThunks";
import { selectViewStats } from "../../../store/slices/viewTracking/slice";
import ProductAnalyticsDonutChart from "../../../components/utils/ProductAnalyticsDonutChart";
import { toast } from "../../../components/modern-ui/sonner";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
} from "../AdminProductsPage/AdminProductsPage.styles";
import BandLayout from "../BandLayout/BandLayout";

const BandDashboardPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const user = useSelector(selectUser);
  const bandAnalytics = useSelector(selectBandAnalytics);
  const bandProductAnalytics = useSelector(selectBandProductAnalytics);
  const bandProducts = useSelector(selectBandProducts);
  const bandOrders = useSelector(selectBandOrders);
  const bandMonthlyRevenues = useSelector(selectMonthlyBandRevenuesByPeriod);
  const viewStats = useSelector(selectViewStats);

  const [orderModal, setOrderModal] = useState({
    show: false,
    order: null,
  });

  useScrollLock(orderModal.show);

  const showOrderDetails = (order) => {
    setOrderModal({ show: true, order });
  };

  // Load initial band data on component mount
  useEffect(() => {
    if (user && user.role === "band" && user?.bandId) {
      // Handle login notification
      if (location.state?.fromLogin) {
        toast.success(`Dobrodošli ${user?.username || "Band Dashboard"}!`);

        // Clear state so it doesn't show again
        window.history.replaceState({}, document.title);
      }
    }
  }, [user?.bandId, location.state]);

  const getStats = () => {
    const products = bandProducts.data?.data || [];
    const analytics = bandAnalytics.data || {};
    const viewData = viewStats.data || {};

    const totalRevenue = analytics.totalRevenue || 0;
    const currentMonthRevenue = analytics.currentMonthRevenue || 0;
    const bandTotalRevenue = analytics.bandTotalRevenue || 0;
    const bandCurrentMonthRevenue = analytics.bandCurrentMonthRevenue || 0;
    const totalProducts = analytics.totalProducts || products?.length;
    const totalOrders = analytics.totalOrders || 0;
    const completedOrders = analytics.completedOrders || 0;

    // View statistics
    const totalViews = viewData.viewStats?.totalViews || 0;

    return {
      totalRevenue,
      currentMonthRevenue,
      bandTotalRevenue,
      bandCurrentMonthRevenue,
      totalProducts,
      totalOrders,
      completedOrders,
      totalViews,
      bestSellingProducts: analytics.bestSellingProducts || [],
    };
  };

  const loadBandData = async () => {
    const bandId = user.bandId;
    const currentYear = new Date().getFullYear();

    if (!bandId) return;

    try {
      await Promise.all([
        dispatch(getBandProducts({ bandId })).unwrap(),
        dispatch(getBandAnalytics(bandId)).unwrap(),
        dispatch(getBandProductAnalytics(bandId)).unwrap(),
        dispatch(getBandViewStats(bandId)).unwrap(),
        dispatch(getCategories()).unwrap(),

        dispatch(
          getMonthlyBandRevenuesByPeriod({
            bandId,
            startYear: currentYear,
            startMonth: 1,
            endYear: currentYear,
            endMonth: 12,
          })
        )
          .unwrap()
          .then(() => {})
          .catch((err) => console.log(err.message)),
      ]);
    } catch (error) {
      console.error("Error loading band data:", error);

      toast.error("Greška pri učitavanju podataka");
    }
  };

  const loadOrdersPage = async (page, statusFilter = "all") => {
    const bandId = user.bandId;
    if (!bandId) return;

    try {
      const params = {
        bandId,
        page,
        limit: 10,
      };

      // Only add status filter if it's not "all"
      if (statusFilter !== "all") {
        params.status = statusFilter;
      }

      await dispatch(getBandOrders(params)).unwrap();
    } catch (error) {
      console.error("Error loading orders page:", error);
      toast.error("Greška pri učitavanju stranice");
    }
  };

  // Load initial band data on component mount
  useEffect(() => {
    if (user && user.role === "band" && user?.bandId) {
      loadBandData();
    }
  }, [user?.bandId]);

  const closeOrderModal = () => {
    setOrderModal({ show: false, order: null });
  };

  // Load orders when currentPage changes OR when orderStatusFilter changes
  useEffect(() => {
    if (user && user.role === "band" && user?.bandId) {
      loadOrdersPage(1, "all");
    }
  }, [user?.bandId]);

  // Clear errors on component unmount
  useEffect(() => {
    return () => {
      dispatch(clearAllErrors());
    };
  }, [dispatch]);

  const getOrderStatusText = (status) => {
    const statusTexts = {
      pending: "Na čekanju",
      processing: "U obradi",
      delivered: "Završeno",
      shipped: "Poslano",
      canceled: "Otkazano",
    };
    return statusTexts[status] || status;
  };

  const getOrderStatusIcon = (status) => {
    switch (status) {
      case "pending":
        return <FiClock />;
      case "processing":
        return <FiActivity />;
      case "delivered":
        return <FiCheck />;
      case "canceled":
        return <FiXCircle />;
      default:
        return <FiClock />;
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div
          style={{
            backgroundColor: "#1a1a1a",
            padding: "10px",
            border: "1px solid #ff6b35",
            borderRadius: "8px",
            color: "#ffffff",
          }}
        >
          <p>{`${label}: ${formatPrice(payload[0].value)}`}</p>
        </div>
      );
    }
    return null;
  };

  const stats = getStats();

  return (
    <BandLayout>
      <PageSection $active>
        {/* Stats Overview */}

        <StatsGrid>
          <StatCard>
            <StatIcon>
              <FiTrendingUp />
            </StatIcon>

            <StatValue>{formatPrice(stats.totalRevenue / 117, true)}</StatValue>
            <StatLabel $eur>{formatPrice(stats.totalRevenue)}</StatLabel>
            <StatLabel>Ukupan Prihod</StatLabel>
            <StatLabel
              style={{
                fontSize: "0.9rem",
                color: "#28a745",
                marginTop: "-0.5rem",
              }}
            >
              Vaš deo: {formatPrice((stats.bandTotalRevenue || 0) / 117, true)}{" "}
              = {formatPrice(stats.bandTotalRevenue || 0)}
            </StatLabel>
          </StatCard>
          <StatCard>
            <StatIcon>
              <FiDollarSign />
            </StatIcon>
            <StatValue>
              {formatPrice(stats.currentMonthRevenue / 117, true)}
            </StatValue>
            <StatLabel $eur>{formatPrice(stats.currentMonthRevenue)}</StatLabel>
            <StatLabel>Zarada ovog meseca</StatLabel>
            <StatLabel
              style={{
                fontSize: "0.9rem",
                color: "#28a745",
                marginTop: "-0.5rem",
              }}
            >
              Vaš deo:{" "}
              {formatPrice((stats.bandCurrentMonthRevenue || 0) / 117, true)} ={" "}
              {formatPrice(stats.bandCurrentMonthRevenue || 0)}
            </StatLabel>
          </StatCard>
        </StatsGrid>

        <StatsGrid>
          <StatCard>
            <StatIcon>
              <FiShoppingCart />
            </StatIcon>
            <StatValue>{stats.totalOrders}</StatValue>
            <StatLabel>Ukupne Porudžbine</StatLabel>
          </StatCard>
          <StatCard>
            <StatIcon>
              <FiPackage />
            </StatIcon>
            <StatValue>{stats.totalProducts}</StatValue>
            <StatLabel>Ukupno Proizvoda</StatLabel>
          </StatCard>
          <StatCard>
            <StatIcon>
              <FiEye />
            </StatIcon>
            <StatValue>{stats.totalViews}</StatValue>
            <StatLabel>Ukupno Pregleda</StatLabel>
          </StatCard>
        </StatsGrid>

        {/* Charts */}
        <ChartsGrid>
          <ChartContainer>
            <ChartTitle>
              <FiTrendingUp />
              Prodaja po Mesecima
            </ChartTitle>
            {bandMonthlyRevenues?.loading ? (
              <LoadingContainer>
                <l-line-spinner
                  size="32"
                  stroke="2.5"
                  speed="1"
                  color="#ff6b35"
                />
                Učitavanje podataka o prodaji...
              </LoadingContainer>
            ) : bandMonthlyRevenues?.data?.length >= 3 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart
                  data={bandMonthlyRevenues.data.map((item) => ({
                    month: new Date(0, item.month - 1).toLocaleDateString(
                      "sr-Latn-RS",
                      { month: "short" }
                    ),
                    revenue: item.totalRevenue,
                  }))}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis dataKey="month" stroke="#cccccc" fontSize={12} />
                  <YAxis
                    stroke="#cccccc"
                    fontSize={12}
                    tickFormatter={(value) => `${value / 1000}k`}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Line
                    type="monotone"
                    dataKey="revenue"
                    stroke="#ff6b35"
                    strokeWidth={3}
                    dot={{ fill: "#ff6b35", strokeWidth: 2, r: 6 }}
                    activeDot={{ r: 8, stroke: "#ff6b35", strokeWidth: 2 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <ChartLockOverlay>
                <ChartBackground />
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart
                    data={bandMonthlyRevenues.data.map((item) => ({
                      month: new Date(0, item.month - 1).toLocaleDateString(
                        "sr-Latn-RS",
                        { month: "short" }
                      ),
                      revenue: item.totalRevenue,
                    }))}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis dataKey="month" stroke="#cccccc" fontSize={12} />
                    <YAxis
                      stroke="#cccccc"
                      fontSize={12}
                      tickFormatter={(value) => `${value / 1000}k`}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Line
                      type="monotone"
                      dataKey="revenue"
                      stroke="#ff6b35"
                      strokeWidth={3}
                      dot={{ fill: "#ff6b35", strokeWidth: 2, r: 6 }}
                      activeDot={{ r: 8, stroke: "#ff6b35", strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
                <ChartBlur />
                <ChartLockIcon>
                  <FiLock />
                  <div className="lock-text">
                    Potrebno je najmanje{" "}
                    <span className="highlight">3 mesećna izveštaja</span>
                    <br />
                    za prikazivanje grafa
                  </div>
                </ChartLockIcon>
              </ChartLockOverlay>
            )}
          </ChartContainer>

          <ChartContainer>
            <ChartTitle>
              <FiTarget />
              Top Proizvodi
            </ChartTitle>
            {bandProductAnalytics?.loading ? (
              <LoadingContainer>
                <l-line-spinner
                  size="32"
                  stroke="2.5"
                  speed="1"
                  color="#ff6b35"
                />
                Učitavanje podataka...
              </LoadingContainer>
            ) : (
              <ProductAnalyticsDonutChart
                bandProductAnalytics={bandProductAnalytics}
              />
            )}
          </ChartContainer>
        </ChartsGrid>

        {/* Recent Orders */}
        <TableContainer>
          <TableHeader>
            <TableTitle>Poslednje Porudžbine</TableTitle>
            <Btn
              className="primary sm"
              onClick={() => {
                navigate("/bend-panel/porudzbine");
                setTimeout(() => {
                  window.scrollTo(0, 0);
                }, 300);
              }}
            >
              Pogledaj Sve
            </Btn>
          </TableHeader>

          {bandOrders.loading ? (
            <LoadingContainer>
              <l-line-spinner
                size="32"
                stroke="2.5"
                speed="1"
                color="#ff6b35"
              />
              Učitavanje porudžbina...
            </LoadingContainer>
          ) : (
            <Table>
              <thead>
                <tr>
                  <TableTh>ID</TableTh>
                  <TableTh>Proizvod</TableTh>
                  <TableTh>Kupac</TableTh>
                  <TableTh>Cena</TableTh>
                  <TableTh>Datum</TableTh>
                  <TableTh>Status</TableTh>
                  <TableTh>Akcije</TableTh>
                </tr>
              </thead>
              <tbody>
                {(bandOrders.data || []).slice(0, 5)?.length === 0 ? (
                  <tr>
                    <TableCell
                      colSpan="7"
                      style={{ textAlign: "center", color: "#666" }}
                    >
                      Nemate porudžbine
                    </TableCell>
                  </tr>
                ) : (
                  (bandOrders.data || []).slice(0, 5).map((order) => (
                    <TableRow key={order.id}>
                      <TableCell>{order.id}</TableCell>
                      <TableCell>
                        {order.orderItems?.[0]?.product?.name || "N/A"}
                      </TableCell>
                      <TableCell>
                        {order.firstName + " " + order.lastName}
                      </TableCell>
                      <TableCell>
                        {formatPrice(
                          order.orderItems.reduce(
                            (acc, item) => acc + item.totalPrice,
                            0
                          )
                        )}
                      </TableCell>

                      <TableCell>
                        {new Date(order.createdAt).toLocaleDateString(
                          "sr-Latn-RS"
                        )}
                      </TableCell>
                      <TableCell>
                        <OrderStatus $status={order.status}>
                          {getOrderStatusIcon(order.status)}
                          {getOrderStatusText(order.status)}
                        </OrderStatus>
                      </TableCell>
                      <TableCell>
                        <Btn
                          className="secondary sm"
                          onClick={() => showOrderDetails(order)}
                        >
                          <FiEye />
                          Detalji
                        </Btn>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </tbody>
            </Table>
          )}
        </TableContainer>
      </PageSection>

      {/* Order Details Modal */}
      <Modal $active={orderModal.show}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Detalji Porudžbine #{orderModal.order?.id}</ModalTitle>
            <CloseBtn onClick={closeOrderModal}>
              <FiX />
            </CloseBtn>
          </ModalHeader>

          {orderModal.order && (
            <>
              {/* Customer Information */}
              <CustomerInfoSection>
                <h4>Informacije o Kupcu</h4>
                <CustomerInfoGrid>
                  <OrderDetailCard>
                    <OrderDetailLabel>
                      <FiUser /> Kupac
                    </OrderDetailLabel>
                    <OrderDetailValue>
                      {orderModal.order.firstName}{" "}
                      {orderModal.order.lastName?.charAt(0)}.
                    </OrderDetailValue>
                  </OrderDetailCard>
                </CustomerInfoGrid>
              </CustomerInfoSection>

              {/* Order Items */}
              <OrderItemsSection>
                <h4>Stavke Porudžbine</h4>
                {orderModal.order.orderItems?.map((item) => (
                  <OrderItemCard key={item.id}>
                    <OrderItemImage>
                      <FiPackage size={40} />
                    </OrderItemImage>
                    <OrderItemInfo>
                      <OrderItemName>{item.name}</OrderItemName>
                      <OrderItemSize>Veličina: {item.size}</OrderItemSize>
                      <OrderItemQuantity>
                        Količina: {item.quantity}
                      </OrderItemQuantity>
                    </OrderItemInfo>
                    <OrderItemPrice>
                      <div>Jedinična cena: {formatPrice(item.itemPrice)}</div>
                      <div>
                        <strong>Ukupno: {formatPrice(item.totalPrice)}</strong>
                      </div>
                    </OrderItemPrice>
                  </OrderItemCard>
                ))}
              </OrderItemsSection>

              {/* Order Summary */}
              <OrderSummarySection>
                <h4>Rezime Porudžbine</h4>
                <OrderSummaryGrid>
                  <OrderDetailCard>
                    <OrderDetailLabel>
                      <FiCalendar /> Datum Narudžbe
                    </OrderDetailLabel>
                    <OrderDetailValue>
                      {new Date(orderModal.order.createdAt).toLocaleDateString(
                        "sr-Latn-RS",
                        {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        }
                      )}
                    </OrderDetailValue>
                  </OrderDetailCard>
                  <OrderDetailCard>
                    <OrderDetailLabel>Status</OrderDetailLabel>
                    <OrderDetailValue>
                      <OrderStatus $status={orderModal.order.status}>
                        {getOrderStatusIcon(orderModal.order.status)}
                        {getOrderStatusText(orderModal.order.status)}
                      </OrderStatus>
                    </OrderDetailValue>
                  </OrderDetailCard>
                  <OrderDetailCard>
                    <OrderDetailLabel>
                      <FiDollarSign /> Ukupna Cena
                    </OrderDetailLabel>
                    <OrderDetailValue>
                      <strong>
                        {formatPrice(orderModal.order.totalPrice)}
                      </strong>
                    </OrderDetailValue>
                  </OrderDetailCard>
                  <OrderDetailCard>
                    <OrderDetailLabel>Vaš Deo</OrderDetailLabel>
                    <OrderDetailValue>
                      <strong>
                        {formatPrice(orderModal.order.bandItemsPrice || 0)}
                      </strong>
                    </OrderDetailValue>
                  </OrderDetailCard>
                </OrderSummaryGrid>
              </OrderSummarySection>
            </>
          )}
        </ModalContent>
      </Modal>
    </BandLayout>
  );
};

export default BandDashboardPage;
